/**
 * <TutorHomePage> — l'accueil du masteur, « Ma journée » (page).
 *
 * La piste A1 du shaping (validée le 28/10) et le livre V16 (p. 45) : l'accueil se lit de haut en
 * bas comme une journée. Ce qu'il montre, c'est l'ancien `TutorHomeTabScreen.js` réordonné — rien
 * n'y est inventé :
 *  1. « Bonjour Camille », la date et les lives du jour, et le bouton qui rafraîchit
 *     (`<TutorHomeGreeting>`, à la place du titre « TABLEAU DE BORD ») ;
 *  2. « À faire · n » : UNE liste à la place des bannières (`<TutorHomeTodo>`), tout en haut — ce
 *     que le masteur a à faire passe avant le reste (Wajih, 29/10) ;
 *  3. « Aujourd'hui » : le live qui vient en grand, avec « Rejoindre » et « Préparer », les suivants
 *     en lignes, chacun avec ses élèves nommés, leur classe et leurs matières, « Toute la semaine »
 *     (`<TutorTodaySection>`, l'ancienne « Mes prochains lives ») ; un jour sans live, les trois
 *     prochains ;
 *  4. l'offre de cours qui attend une réponse, « Accepter » et « Voir » (`<TutorHomeOffer>`) ;
 *  5. « Mon score », rhabillé en sourdine, à la place réservée à « Mon parcours » (`<TutorScoreCard>`) ;
 *  6. « Mon mois » et ses paiements (`<TutorEarningsCard>`, l'ancienne « Mon historique »).
 * En deux colonnes à partir de 760 px de bloc, une pile sur un téléphone (`<TutorHomeColumns>`,
 * qui tient l'ordre). Sans chip « Préparée » : la préparation est automatique (Wajih, 28/10).
 *
 * Ce qui manque ne prend pas de place : pas d'offre, pas de carte ; rien à faire, pas de section ;
 * pas de score (un compte qui n'est pas masteur), pas de carte. Le compte qui n'a pas le droit de
 * voir des élèves garde ce qu'il a à faire, et « Bientôt disponible » à la place du reste, comme
 * l'ancien écran.
 *
 * Trois boîtes s'ouvrent sur l'accueil, et c'est l'écran qui dit laquelle :
 *  - le rappel mensuel « Confirme tes créneaux » (`availabilityReminder`, l'ancien
 *    `MaDispoMonthlyReminder`) ;
 *  - « Nouveau mois, nouveau départ » (`monthReset`, l'ancienne `TutorRankingResetModal`) ;
 *  - la boîte d'une séance qui porte déjà une demande (`sessionNotice`, l'ancien bouton « Gérer les
 *    RDV » d'une carte de live, et le refus d'annuler un remplacement).
 *
 * La lueur au teal est posée par la coquille (`<ScreenBackground glow>`), pas par la page.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { EmptyState } from "../../molecules/EmptyState"
import { List } from "../../molecules/List"
import { Snackbar } from "../../molecules/Snackbar"
import { TutorEarningsCard } from "../../organisms/TutorEarningsCard"
import { TutorScoreCard } from "../../organisms/TutorScoreCard"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { TutorMonthResetDialog } from "../../templates/TutorMonthResetDialog"
import { TutorSessionNoticeDialog, type TutorSessionNotice } from "../../templates/TutorSessionNoticeDialog"
import { TutorTodaySection } from "../../templates/TutorTodaySection"
import {
    type UITutorEarnings,
    type UITutorMonthReset,
    type UITutorScore,
    type UITutorSession,
    type UITutorTodo,
} from "../../../ports/primary/ui/types/tutorCoquilleAccueil"
import { type UITutorOffer } from "../../../ports/primary/ui/types/tutorDispoOffres"
import { TutorHomeColumns } from "./TutorHomeColumns"
import { TutorHomeGreeting } from "./TutorHomeGreeting"
import { TutorHomeOffer } from "./TutorHomeOffer"
import { TutorHomeSkeleton } from "./TutorHomeSkeleton"
import { TutorHomeTodo } from "./TutorHomeTodo"

export type TutorHomePageProps = {
    /** « Bonjour Camille ». Absent : pas de tête. */
    firstname?: string
    refreshing?: boolean
    onRefresh?: () => void
    /** Un geste refusé (« Rafraîchir », « Mes paiements »…), dit en bas de page (`<Snackbar>`) : le message du serveur. `null` : rien. */
    notice?: string | null
    onDismissNotice?: () => void

    /** Le compte voit des élèves (`my_students`). `false` : « Bientôt disponible » à la place des lives, du score et du mois. Défaut `true`. */
    canSeeStudents?: boolean

    /** Les lives du jour, dans l'ordre. */
    todaySessions?: UITutorSession[]
    /** Un jour sans live : les prochains. */
    upcomingSessions?: UITutorSession[]
    onJoinSession?: (session: UITutorSession) => void
    onPrepareSession?: (session: UITutorSession) => void
    onSelectSession?: (session: UITutorSession) => void
    onOpenWeek?: () => void

    /** L'offre de cours en attente la plus récente. `null` : pas de carte. */
    offer?: UITutorOffer | null
    /** Les offres en attente : au-delà d'une, « Toutes les offres ». */
    pendingOffersCount?: number
    onAcceptOffer?: (offer: UITutorOffer) => void
    onOpenOffer?: (offer: UITutorOffer) => void
    onOpenOffers?: () => void

    /** Ce qui attend le masteur, dans l'ordre. Vide : pas de section. */
    todos?: UITutorTodo[]
    onTodoPress?: (todo: UITutorTodo) => void

    /** « Mon score ». `null` : pas de carte. */
    score?: UITutorScore | null
    /** Le mois et ses paiements. `null` : pas de carte. */
    earnings?: UITutorEarnings | null
    onOpenPayments?: () => void
    openingPayments?: boolean
    onOpenLivesHistory?: () => void

    /** Le rappel mensuel de « Ma dispo » est ouvert. */
    availabilityReminder?: boolean
    onConfirmAvailabilityReminder?: () => void
    onDismissAvailabilityReminder?: () => void
    /** Le rang du mois passé, à la première visite du mois. `null` : fermé. */
    monthReset?: UITutorMonthReset | null
    onCloseMonthReset?: () => void
    /** La boîte d'une séance qui porte déjà une demande. `null` : fermée. */
    sessionNotice?: { notice: TutorSessionNotice; session: UITutorSession; lateMinutes?: number } | null
    onCancelSessionRequest?: () => void
    onContactSupport?: () => void
    onCloseSessionNotice?: () => void

    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

const noop = () => {}

export function TutorHomePage(props: TutorHomePageProps) {
    const {
        firstname,
        refreshing,
        onRefresh,
        notice = null,
        onDismissNotice = noop,
        canSeeStudents = true,
        todaySessions = [],
        upcomingSessions = [],
        onJoinSession,
        onPrepareSession,
        onSelectSession,
        onOpenWeek,
        offer = null,
        pendingOffersCount,
        onAcceptOffer,
        onOpenOffer,
        onOpenOffers,
        todos = [],
        onTodoPress,
        score = null,
        earnings = null,
        onOpenPayments,
        openingPayments,
        onOpenLivesHistory,
        availabilityReminder = false,
        onConfirmAvailabilityReminder = noop,
        onDismissAvailabilityReminder = noop,
        monthReset = null,
        onCloseMonthReset = noop,
        sessionNotice = null,
        onCancelSessionRequest,
        onContactSupport,
        onCloseSessionNotice = noop,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const todo = <TutorHomeTodo todos={todos} onTodoPress={onTodoPress} />

    return (
        <>
            <PageTemplate
                refreshing={refreshing}
                onRefresh={onRefresh}
                testID={testID}
                loading={loading}
                skeleton={<TutorHomeSkeleton firstname={firstname} />}
                error={error}
                onRetry={onRetry}
                padding="4"
                maxContentWidth={PARENT_CONTENT_WIDTH}
            >
                <Stack gap="6">
                    {firstname != null && firstname !== "" && (
                        <TutorHomeGreeting
                            firstname={firstname}
                            livesToday={todaySessions.length}
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                        />
                    )}

                    {canSeeStudents ? (
                        <TutorHomeColumns
                            today={
                                <TutorTodaySection
                                    sessions={todaySessions}
                                    upcoming={upcomingSessions}
                                    onJoinSession={onJoinSession}
                                    onPrepareSession={onPrepareSession}
                                    onSelectSession={onSelectSession}
                                    onOpenWeek={onOpenWeek}
                                />
                            }
                            offer={
                                offer != null ? (
                                    <TutorHomeOffer
                                        offer={offer}
                                        pendingCount={pendingOffersCount}
                                        onAccept={onAcceptOffer}
                                        onOpen={onOpenOffer}
                                        onOpenAll={onOpenOffers}
                                    />
                                ) : undefined
                            }
                            todo={todos.length > 0 ? todo : undefined}
                            score={
                                score != null ? (
                                    <List title="Mon score">
                                        <TutorScoreCard score={score} />
                                    </List>
                                ) : undefined
                            }
                            month={
                                earnings != null ? (
                                    <TutorEarningsCard
                                        earnings={earnings}
                                        onOpenPayments={onOpenPayments}
                                        openingPayments={openingPayments}
                                        onOpenLivesHistory={onOpenLivesHistory}
                                    />
                                ) : undefined
                            }
                        />
                    ) : (
                        <Stack gap="6">
                            {todo}
                            <EmptyState title="Bientôt disponible" />
                        </Stack>
                    )}
                </Stack>
            </PageTemplate>

            <ConfirmDialog
                visible={availabilityReminder}
                title="Confirme tes créneaux"
                message="Une fois par semaine, pour continuer à recevoir des propositions de groupes."
                confirmLabel="Confirmer"
                cancelLabel="Plus tard"
                danger={false}
                onConfirm={onConfirmAvailabilityReminder}
                onCancel={onDismissAvailabilityReminder}
            />
            <TutorMonthResetDialog reset={monthReset} onClose={onCloseMonthReset} />
            <TutorSessionNoticeDialog
                notice={sessionNotice?.notice ?? null}
                session={sessionNotice?.session ?? null}
                lateMinutes={sessionNotice?.lateMinutes}
                onCancelRequest={onCancelSessionRequest}
                onContactSupport={onContactSupport}
                onClose={onCloseSessionNotice}
            />
            <Snackbar visible={notice != null} message={notice ?? ""} tone="danger" onDismiss={onDismissNotice} />
        </>
    )
}
